import { isAgentCapabilities, type AgentCapabilities } from '@borgee/agent-remote-protocol';
import { captureSessionControlObservation, getSessionActivity, matchesSessionControlObservation } from '@borgee/remote-control-client';
import { validSessionControlOperation, type SessionControlInput, type SessionControlOperation, type SessionControlResponse, type SessionControlTarget } from '@borgee/plugin-sdk/session-control';
import type { DshSessionView } from '../stores/useDshSessionStore';
import { randomUUID } from './uuid';

export interface SessionControlContext {
  agentId: string;
  channelId: string;
  canManage: boolean;
  connectionEpoch: number;
  controlGeneration?: string;
  session?: DshSessionView;
}

function matchesSessionControlIdentity(target: SessionControlTarget | undefined, context: SessionControlContext): target is SessionControlTarget {
  const session = context.session;
  return !!target && !!session && target.connection_generation === context.controlGeneration && context.canManage
    && target.agent_id === context.agentId && target.channel_id === context.channelId
    && target.binding_id === session.sessionControlBindingId && target.native_session_id === session.runtimeInfo?.sessionId
    && !['failed', 'closed'].includes(session.runtimeInfo?.status ?? '');
}

export function matchesSessionControlTarget(target: SessionControlTarget | undefined, context: SessionControlContext): target is SessionControlTarget {
  return matchesSessionControlIdentity(target, context) && context.session?.nativeCapabilitiesLive === true;
}

export function sessionControlCapabilities(target: SessionControlTarget | undefined, context: SessionControlContext): Pick<AgentCapabilities, 'sessionSettings' | 'planning' | 'commands'> {
  const live = matchesSessionControlTarget(target, context);
  const native = context.session?.nativeCapabilities;
  const advertised = target && isAgentCapabilities(target.capabilities) ? target.capabilities : undefined;
  return {
    sessionSettings: live && native?.sessionSettings === true && advertised?.sessionSettings === true,
    planning: live && native?.planning === true && advertised?.planning === true,
    commands: live && native?.commands === true && advertised?.commands === true,
  };
}

export function sessionInputCapabilities(target: SessionControlTarget | undefined, context: SessionControlContext): Pick<AgentCapabilities, 'steer' | 'queueMessage'> {
  const live = matchesSessionControlTarget(target, context);
  const native = context.session?.nativeCapabilities;
  const advertised = target && isAgentCapabilities(target.capabilities) ? target.capabilities : undefined;
  return {
    steer: live && native?.steer === true && advertised?.steer === true && target?.input_operations?.includes('steer') === true,
    queueMessage: live && native?.queueMessage === true && advertised?.queueMessage === true && target?.input_operations?.includes('next_turn') === true,
  };
}

export class SessionControlError extends Error {
  constructor(readonly reason: 'unavailable' | 'unsupported' | 'busy' | 'unconfirmed' | 'rejected', readonly result?: SessionControlResponse) {
    super(reason);
    this.name = 'SessionControlError';
  }
}

export async function invokeNormalizedSessionControl(
  target: SessionControlTarget,
  operation: SessionControlOperation,
  context: () => SessionControlContext,
  apply: (channelId: string, agentId: string, input: SessionControlInput, signal: AbortSignal, expiresAt: number) => Promise<SessionControlResponse>,
): Promise<SessionControlResponse> {
  const before = context();
  const connectionEpoch = before.connectionEpoch;
  const observation = captureSessionControlObservation(before.session);
  if (!matchesSessionControlTarget(target, before)) throw new SessionControlError('unavailable');
  if (!validSessionControlOperation(operation) || operation.kind === 'list_pending_inputs') throw new SessionControlError('unsupported');
  if (operation.kind === 'send_input') {
    const inputs = sessionInputCapabilities(target, before);
    if (!(operation.delivery === 'steer' ? inputs.steer : inputs.queueMessage)) throw new SessionControlError('unsupported');
    if (operation.delivery === 'steer' && (!before.session?.active || before.session.turnId !== operation.expected_turn_id)) throw new SessionControlError('unavailable');
  } else {
    const capabilities = sessionControlCapabilities(target, before);
    const capability = operation.kind === 'set_setting' ? 'sessionSettings' : operation.kind === 'set_planning' ? 'planning' : 'commands';
    if (!capabilities[capability]) throw new SessionControlError('unsupported');
    if (operation.kind !== 'list_commands' && getSessionActivity(before.session).busy) throw new SessionControlError('busy');
  }
  const input: SessionControlInput = { version: 1, operation_id: randomUUID(), binding_id: target.binding_id, native_session_id: target.native_session_id, connection_generation: target.connection_generation, operation };
  const controller = new AbortController();
  const expiresAt = Date.now() + 30000;
  const timer = setTimeout(() => controller.abort(), Math.max(0, expiresAt - Date.now()));
  try {
    const result = await apply(before.channelId, before.agentId, input, controller.signal, expiresAt);
    const after = context();
    if (after.connectionEpoch !== connectionEpoch || !matchesSessionControlIdentity(target, after)
      || !matchesSessionControlObservation(observation, after.session)) throw new SessionControlError('unconfirmed', result);
    if (result.status !== 'confirmed') throw new SessionControlError(result.status, result);
    return result;
  } catch (error) {
    if (error instanceof SessionControlError) throw error;
    throw new SessionControlError('unconfirmed');
  } finally { clearTimeout(timer); }
}

/** A native link selects only a unique, independently published authorized binding. */
export function selectNativeSessionScope(scope: import('./dsh-session-journal').DshSessionScope, nativeSessionId: string, sessions: Record<string, DshSessionView>): import('./dsh-session-journal').DshSessionScope | undefined {
  const matches = Object.entries(sessions).filter(([key, session]) => {
    const [account, guild, channel, agent, binding] = JSON.parse(key);
    return account === scope.accountId && guild === scope.guildId && channel === scope.channelId && agent === scope.agentId && typeof binding === 'string'
      && binding === session.sessionControlBindingId && session.nativeCapabilitiesLive === true && session.runtimeInfo?.sessionId === nativeSessionId;
  });
  if (matches.length !== 1) return;
  return { ...scope, bindingId: matches[0]![1].sessionControlBindingId };
}

export async function getNormalizedSessionTarget(scope: import('./dsh-session-journal').DshSessionScope, captured?: { token: string; taskId: string; requestId: string }): Promise<SessionControlTarget> {
  const { getSessionControlTarget } = await import('./api');
  const { useDshSessionStore, dshSessionKey } = await import('../stores/useDshSessionStore');
  const controller = new AbortController();
  const expiresAt = Date.now() + 15000;
  const timer = setTimeout(() => controller.abort(), Math.max(0, expiresAt - Date.now()));
  try {
    const target = await getSessionControlTarget(scope.channelId, scope.agentId, controller.signal, scope.bindingId, expiresAt);
    const session = useDshSessionStore.getState().sessions[dshSessionKey(scope)];
    if (session?.nativeCapabilitiesLive !== true || session.sessionControlBindingId !== target.binding_id || session.runtimeInfo?.sessionId !== target.native_session_id || scope.bindingId && scope.bindingId !== target.binding_id) throw new SessionControlError('unavailable');
    if (captured && !session.pending.some(pending => pending.token === captured.token && pending.taskId === captured.taskId && pending.request.requestId === captured.requestId)) throw new SessionControlError('unavailable');
    return target;
  } finally { clearTimeout(timer); }
}

/** Task controls retain the source Session even when the channel default changes. */
export async function getNormalizedTaskTarget(channelId: string, agentId: string, taskId: string, captured?: { token: string; requestId: string }): Promise<SessionControlTarget> {
  const { useNormalizedActivityStore } = await import('../stores/useNormalizedActivityStore');
  const { resolveNormalizedSessionScope } = await import('./normalized-session-scope');
  const current = () => useNormalizedActivityStore.getState().byChannel[channelId]?.[agentId]?.[taskId];
  const scope = current()?.sessionScope;
  const owner = resolveNormalizedSessionScope(channelId, agentId);
  if (!scope?.bindingId || !owner || owner.accountId !== scope.accountId || owner.guildId !== scope.guildId || scope.channelId !== channelId || scope.agentId !== agentId) throw new SessionControlError('unavailable');
  const target = await getNormalizedSessionTarget(scope, captured ? { ...captured, taskId } : undefined);
  if (JSON.stringify(current()?.sessionScope) !== JSON.stringify(scope)) throw new SessionControlError('unavailable');
  return target;
}

export function stoppableNormalizedTask(tasks: Record<string, import('../stores/useNormalizedActivityStore').NormalizedTaskState>): string | undefined {
  const active = Object.entries(tasks).filter(([, task]) => task.active);
  if (active.length !== 1) return;
  const [id, task] = active[0]!;
  return task.sessionScope?.bindingId && task.nativeCapabilitiesLive && task.nativeCapabilities?.cancel ? id : undefined;
}
